import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createAttempt, submitReading } from '../../api/exams';
import { useAuth } from '../../context/AuthContext';
import { setPartProgress, computeMistakes, scoreReadingPart } from './_progress';
import { T } from './_theme';
import { ReadingFrame, PrimaryBtn, GhostBtn, NudgeBanner, Pill, readingHomeHref } from './_primitives';
import ExamReport from './ExamReport';

// FET Reading — Exam mode (5 parts under one global timer).
//
// Resume support: state (current question + answers + start time) is mirrored
// into localStorage every change, so closing the tab and coming back drops
// the student back where they were with the timer continuing wall-clock.

const EXAM_DURATION_SECONDS = 45 * 60;

const examStateKey = (userId, examId) =>
  `fet_reading_exam_${userId || 'anon'}_${examId || ''}`;

const loadExamState = (userId, examId) => {
  try {
    const raw = window.localStorage.getItem(examStateKey(userId, examId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch { return null; }
};

const saveExamState = (userId, examId, payload) => {
  try { window.localStorage.setItem(examStateKey(userId, examId), JSON.stringify(payload)); } catch {}
};

const clearExamState = (userId, examId) => {
  try { window.localStorage.removeItem(examStateKey(userId, examId)); } catch {}
};

const fmtTime = (s) => {
  const m = Math.floor(s / 60).toString().padStart(2, '0');
  const ss = Math.floor(s % 60).toString().padStart(2, '0');
  return `${m}:${ss}`;
};

// ── Question-count + answered-count helpers per part ──
function countQuestions(part) {
  const c = part?.content || {};
  switch (part?.part_number) {
    case 1: return (c.signs || []).length;
    case 2: return (c.gaps || []).length;
    case 3: return (c.questions || []).length;
    case 4: return (c.items || []).length;
    case 5: return (c.questions || []).length;
    default: return 0;
  }
}
function countAnswered(part, ans) {
  const c = part?.content || {};
  let n = 0;
  switch (part?.part_number) {
    case 1: (c.signs || []).forEach((_x, i) => { if (ans[`sign_${i}`]) n += 1; }); return n;
    case 2: (c.gaps || []).forEach((_x, i) => { if (ans[`gap_${i}`]) n += 1; }); return n;
    case 3: (c.questions || []).forEach((_x, i) => { if (ans[`q_${i}`]) n += 1; }); return n;
    case 4: (c.items || []).forEach((_x, i) => { if (ans[`q_${i}`]) n += 1; }); return n;
    case 5: (c.questions || []).forEach((_x, i) => { if (ans[`q_${i}`]) n += 1; }); return n;
    default: return 0;
  }
}

export default function ExamModeScreen({
  isMobile, navigate, exam, loading,
}) {
  const { user } = useAuth();
  const userId = user?.id;
  const examId = exam?.id;

  // Defensive clamp: only render parts 1–5. The new schema removed Part 6
  // (Open Cloze) but legacy DB rows still have it on production until the
  // backend migration runs. Without this filter the exam strip shows 30+
  // questions and the student gets stuck at "PART 6 · Q31" with no renderer.
  const parts = useMemo(
    () => (exam?.reading_parts || [])
      .filter((p) => p.has_content)
      .filter((p) => Number(p.part_number) >= 1 && Number(p.part_number) <= 5)
      .sort((a, b) => a.part_number - b.part_number),
    [exam],
  );

  const initialState = useMemo(() => {
    if (!userId || !examId) return null;
    const saved = loadExamState(userId, examId);
    if (!saved || saved.examId !== examId) return null;
    const startedAt = Number(saved.startedAt);
    if (!Number.isFinite(startedAt)) return null;
    const elapsed = Math.floor((Date.now() - startedAt) / 1000);
    const remaining = EXAM_DURATION_SECONDS - elapsed;
    if (remaining <= 0) {
      clearExamState(userId, examId);
      return null;
    }
    // Clamp the saved partIdx/qIdx to the new (filtered) parts list. A
    // resume saved at "Part 6 · Q31" would otherwise crash or render
    // nothing — point those students at the LAST question of the LAST
    // valid part instead so they can review/submit.
    const partsCount = parts.length;
    let savedPartIdx = Number.isFinite(Number(saved.partIdx)) ? Number(saved.partIdx) : 0;
    let savedQIdx = Number.isFinite(Number(saved.qIdx)) ? Number(saved.qIdx) : 0;
    if (savedPartIdx >= partsCount) savedPartIdx = Math.max(0, partsCount - 1);
    const partTotal = partsCount > 0 ? countQuestions(parts[savedPartIdx]) : 0;
    if (savedQIdx >= partTotal) savedQIdx = Math.max(0, partTotal - 1);
    return {
      partIdx: savedPartIdx,
      qIdx: savedQIdx,
      answersByPart: (saved.answersByPart && typeof saved.answersByPart === 'object') ? saved.answersByPart : {},
      startedAt,
      remaining,
    };
  }, [userId, examId, parts]);

  const [partIdx, setPartIdx] = useState(() => initialState?.partIdx ?? 0);
  const [qIdx, setQIdx] = useState(() => initialState?.qIdx ?? 0);
  const [answersByPart, setAnswersByPart] = useState(() => initialState?.answersByPart ?? {});
  const [secondsLeft, setSecondsLeft] = useState(() => initialState?.remaining ?? EXAM_DURATION_SECONDS);
  const [resumed] = useState(() => !!initialState);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const submittedRef = useRef(false);
  const startedAtRef = useRef(initialState?.startedAt ?? Date.now());

  // Persist state on change.
  useEffect(() => {
    if (!userId || !examId) return;
    if (result || submitting) return;
    const totalQuestions = parts.reduce((sum, p) => sum + countQuestions(p), 0);
    const answeredCount = parts.reduce(
      (sum, p) => sum + countAnswered(p, answersByPart[String(p.part_number)] || {}),
      0,
    );
    saveExamState(userId, examId, {
      examId, partIdx, qIdx, answersByPart,
      startedAt: startedAtRef.current,
      savedAt: Date.now(),
      totalQuestions, answeredCount,
      version: 2,
    });
  }, [userId, examId, parts, partIdx, qIdx, answersByPart, result, submitting]);

  const updateAnswer = (partNum, key, value) => {
    setAnswersByPart((prev) => ({
      ...prev,
      [String(partNum)]: { ...(prev[String(partNum)] || {}), [key]: value },
    }));
  };

  const handleSubmit = async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    setError('');
    const durationSeconds = Math.max(0, Math.floor((Date.now() - startedAtRef.current) / 1000));

    // Compute the result client-side from the canonical 5-part content
    // shape — the production backend's old scoring code returns 0 for the
    // new fields (`gaps`, `questions`, `items`) and even mis-scores Part 1
    // because it reads `sign.answer` instead of `sign.correct`. We trust
    // the client; the backend createAttempt + submitReading still run for
    // persistence but their score is ignored.
    let localTotal = 0;
    let localMax = 0;
    const localPartScores = parts.map((p) => {
      const partAnswers = answersByPart[String(p.part_number)] || {};
      const { score, max } = scoreReadingPart(p.part_number, p.content, partAnswers);
      localTotal += score;
      localMax += max;
      setPartProgress(p.part_number, {
        score, max,
        mistakes: computeMistakes(p.part_number, p.content, partAnswers),
      });
      return { part_number: p.part_number, score, max };
    });
    const localPercentage = localMax > 0 ? Math.round((localTotal / localMax) * 100) : 0;

    try {
      const attempt = await createAttempt(exam.id, 'exam', 'reading');
      await submitReading(attempt.id, answersByPart);
      clearExamState(userId, examId);
    } catch (err) {
      // Backend persist failed — keep the local result so the student
      // still sees their score, just note the failure inline.
      setError(err?.response?.data?.error || 'Could not save your exam to the server. Your local score is shown.');
    } finally {
      setSubmitting(false);
      setResult({
        total: localTotal,
        max: localMax,
        percentage: localPercentage,
        part_scores: localPartScores,
        durationSeconds,
      });
    }
  };

  // Wall-clock timer.
  useEffect(() => {
    if (result || submitting) return undefined;
    const tick = () => {
      const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1000);
      const remaining = Math.max(0, EXAM_DURATION_SECONDS - elapsed);
      setSecondsLeft(remaining);
      if (remaining <= 0) handleSubmit();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [result, submitting]);

  if (loading || !exam) {
    return <ReadingFrame isMobile={isMobile}><div style={{ padding: 32, color: T.ink2, fontFamily: T.sans }}>Loading reading exam…</div></ReadingFrame>;
  }
  if (!parts.length) {
    return (
      <ReadingFrame isMobile={isMobile}>
        <div style={{ padding: 24 }}>
          <div style={{ fontWeight: 700, marginBottom: 6, color: T.ink, fontFamily: T.sans }}>Exam isn't ready</div>
          <div style={{ color: T.ink2, fontFamily: T.sans }}>An admin needs to fill in content for at least one reading part.</div>
        </div>
      </ReadingFrame>
    );
  }

  // Plan check — block direct entry to the timed exam if the student's
  // plan doesn't include reading. Covers bookmarks, deep links, and
  // resumed attempts that bypass ReadingHome's modal.
  const readingAllowed = (() => {
    if (!user) return false;
    if (user.capabilities && typeof user.capabilities.reading === 'boolean') return user.capabilities.reading;
    return ['promo', 'basic', 'ai'].includes(String(user.effective_plan || user.plan || 'free').toLowerCase());
  })();
  if (!readingAllowed) {
    // Wipe any saved exam state so the student doesn't keep getting bounced
    // back into a phantom in-progress exam after an upgrade prompt.
    if (userId && examId) clearExamState(userId, examId);
    return (
      <ReadingFrame isMobile={isMobile}>
        <div style={{ padding: 24, display: 'grid', placeItems: 'center', minHeight: '70vh' }}>
          <div style={{
            background: T.surface, borderRadius: 18,
            border: `1px solid ${T.hairline}`,
            maxWidth: 460, width: '100%', padding: 24,
            fontFamily: T.sans,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <span style={{
                width: 42, height: 42, borderRadius: 12,
                background: T.blueBg, color: T.blue,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, flexShrink: 0,
              }} aria-hidden="true">🔒</span>
              <div>
                <div style={{ fontSize: 11, color: T.ink3, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.08em' }}>
                  Reading · Exam mode
                </div>
                <div style={{ fontSize: 18, fontWeight: 800, color: T.ink, lineHeight: 1.25, marginTop: 2 }}>
                  Plan upgrade required
                </div>
              </div>
            </div>
            <div style={{ fontSize: 14, color: T.ink2, lineHeight: 1.6, marginBottom: 18 }}>
              Reading exams aren't part of your current plan. Upgrade once and you'll unlock every reading exam on the platform. You can keep practising for free in the meantime.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <GhostBtn onClick={() => navigate(readingHomeHref())}>Back to practice</GhostBtn>
              <PrimaryBtn full={false} onClick={() => navigate('/pricing?locked=reading&return_to=/dashboard')}>
                🔒 View plans
              </PrimaryBtn>
            </div>
          </div>
        </div>
      </ReadingFrame>
    );
  }

  if (result) return (
    <ExamReport
      result={result}
      parts={parts}
      answersByPart={answersByPart}
      isMobile={isMobile}
      navigate={navigate}
    />
  );

  const part = parts[partIdx];
  const partAnswers = answersByPart[String(part.part_number)] || {};
  const partTotalQuestions = countQuestions(part);
  const partAnsweredCount = countAnswered(part, partAnswers);
  const isLastPart = partIdx === parts.length - 1;
  const isLastQ = qIdx === partTotalQuestions - 1;
  const totalQuestions = parts.reduce((sum, p) => sum + countQuestions(p), 0);
  const answeredCount = parts.reduce((sum, p) => sum + countAnswered(p, answersByPart[String(p.part_number)] || {}), 0);
  const timerLow = secondsLeft <= 5 * 60;

  // Build a global question index for the strip nav (1..30).
  const globalQNumber = (() => {
    let n = 0;
    for (let i = 0; i < partIdx; i += 1) n += countQuestions(parts[i]);
    return n + qIdx + 1;
  })();
  const allQuestionsAnswered = answeredCount >= totalQuestions;
  // Has the student answered the question they're currently looking at?
  // Drives the Next-button gating: no skipping forward without answering.
  const currentQuestionAnswered = hasAnswerForIdx(part, partAnswers, qIdx);

  const goNext = () => {
    if (!currentQuestionAnswered) return;
    if (!isLastQ) { setQIdx((i) => i + 1); return; }
    if (isLastPart) {
      setShowSubmitConfirm(true);
      return;
    }
    setPartIdx((i) => i + 1);
    setQIdx(0);
  };
  const goPrev = () => {
    if (qIdx > 0) { setQIdx((i) => i - 1); return; }
    if (partIdx > 0) {
      const prevPart = parts[partIdx - 1];
      setPartIdx((i) => i - 1);
      setQIdx(Math.max(0, countQuestions(prevPart) - 1));
    }
  };

  // Each question in the strip is one of: answered, current, pending.
  const stripCells = (() => {
    const cells = [];
    let runningIdx = 0;
    parts.forEach((p, pi) => {
      const partTotal = countQuestions(p);
      for (let qi = 0; qi < partTotal; qi += 1) {
        const ans = answersByPart[String(p.part_number)] || {};
        const isAnswered = countAnswered({ ...p, content: p.content }, ans) > qi // crude — see below
          || hasAnswerForIdx(p, ans, qi);
        const isCurrent = pi === partIdx && qi === qIdx;
        cells.push({ partNumber: p.part_number, qi, n: ++runningIdx, answered: isAnswered, current: isCurrent });
      }
    });
    return cells;
  })();

  return (
    <ReadingFrame isMobile={isMobile}>
      {/* Top bar — exam badge + timer + question strip */}
      <div style={{ background: T.surface, borderBottom: `1px solid ${T.hairline}` }}>
        <div style={{ padding: '12px 16px 8px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            onClick={() => setShowLeaveConfirm(true)}
            aria-label="Back"
            style={{
              width: 30, height: 30, borderRadius: 15,
              background: T.bgSunk, border: 'none',
              display: 'grid', placeItems: 'center', color: T.ink2, fontSize: 12,
              cursor: 'pointer', fontFamily: T.sans,
            }}
          >‹</button>
          <Pill tone="miss" size="m">● EXAM · NO HELP</Pill>
          <div style={{ flex: 1 }} />
          <div style={{
            fontFamily: T.mono, fontSize: 17, fontWeight: 700,
            color: timerLow ? T.miss : T.ink, fontVariantNumeric: 'tabular-nums',
          }}>{fmtTime(secondsLeft)}</div>
        </div>
        <div style={{
          padding: '6px 16px 10px',
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(totalQuestions, 10)}, 1fr)`,
          gap: 4,
        }}>
          {stripCells.map((cell, i) => {
            // Allow jumping back to any earlier question (review) or to any
            // already-answered question. Block forward jumps to questions
            // the student hasn't answered yet — keeps the no-skip rule
            // consistent with the Next button.
            const targetPartIdx = parts.findIndex((p) => p.part_number === cell.partNumber);
            const isPast = targetPartIdx < partIdx || (targetPartIdx === partIdx && cell.qi <= qIdx);
            const locked = !cell.current && !cell.answered && !isPast && !currentQuestionAnswered;
            return (
              <button
                key={i}
                type="button"
                onClick={() => {
                  if (locked) return;
                  if (targetPartIdx >= 0) {
                    setPartIdx(targetPartIdx);
                    setQIdx(cell.qi);
                  }
                }}
                disabled={locked}
                title={locked ? 'Answer the current question first.' : ''}
                style={{
                  height: 20, fontSize: 9, fontWeight: 700,
                  fontFamily: T.mono, display: 'grid', placeItems: 'center',
                  background: cell.current ? T.blue : cell.answered ? T.blueBg : T.bgSunk,
                  color: cell.current ? '#fff' : cell.answered ? T.blueInk : T.ink3,
                  border: cell.current ? `1px solid ${T.blue}` : `1px solid ${T.hairline}`,
                  borderRadius: 4,
                  cursor: locked ? 'not-allowed' : 'pointer',
                  opacity: locked ? 0.55 : 1,
                  padding: 0,
                }}
              >{cell.n}</button>
            );
          })}
        </div>
      </div>

      {resumed && (
        <div style={{ padding: '8px 16px 0' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: T.okBg, border: `1px solid ${T.okEdge}`,
            color: T.ok, borderRadius: 12, padding: '8px 14px',
            fontSize: 12, fontWeight: 700, fontFamily: T.sans,
          }}>
            <span aria-hidden="true">↩</span>
            <span>Welcome back — your answers and the timer are restored.</span>
          </div>
        </div>
      )}

      {/* Question body */}
      <div style={{ flex: 1, padding: '14px 16px 16px', overflow: 'auto' }}>
        <div style={{ fontSize: 11, color: T.ink3, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, fontFamily: T.sans }}>
          Part {part.part_number} · {partLabel(part.part_number)} · Q{globalQNumber}
        </div>
        <PartQuestion
          part={part}
          qIdx={qIdx}
          answers={partAnswers}
          onAnswer={(key, value) => updateAnswer(part.part_number, key, value)}
        />
        {error && <div style={{ marginTop: 12, fontSize: 13, color: T.miss }}>{error}</div>}
        {!currentQuestionAnswered && (
          <div style={{ marginTop: 14 }}>
            <NudgeBanner>
              Pick / write an answer above to continue {isLastPart && isLastQ ? 'and submit your exam.' : 'to the next question.'}
            </NudgeBanner>
          </div>
        )}
      </div>

      {/* Footer nav */}
      <div style={{ padding: '12px 16px', borderTop: `1px solid ${T.hairline}`, background: T.surface, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <GhostBtn
          disabled={partIdx === 0 && qIdx === 0}
          onClick={goPrev}
        >← Prev</GhostBtn>
        <div style={{ flex: 1 }} />
        {/* Always-available Submit when every question is answered. The
            student might be reviewing an earlier question — they shouldn't
            need to navigate to the very last cell just to find Submit. */}
        {allQuestionsAnswered && !(isLastPart && isLastQ) && (
          <GhostBtn onClick={() => setShowSubmitConfirm(true)} disabled={submitting}>
            Submit exam ✓
          </GhostBtn>
        )}
        <PrimaryBtn full={false} disabled={submitting || !currentQuestionAnswered} onClick={goNext}>
          {submitting ? 'Submitting…' : isLastPart && isLastQ ? 'Submit exam ✓' : 'Next →'}
        </PrimaryBtn>
      </div>

      {showLeaveConfirm && (
        <ModalOverlay onClose={() => setShowLeaveConfirm(false)}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6, color: T.ink, fontFamily: T.sans }}>Leave the exam?</div>
          <div style={{ fontSize: 13, color: T.ink2, lineHeight: 1.5, marginBottom: 16, fontFamily: T.sans }}>
            Your answers and the remaining time are saved — you can come back and pick up where you left off. Note: the exam clock keeps running while you're away.
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <GhostBtn onClick={() => setShowLeaveConfirm(false)}>Keep going</GhostBtn>
            <PrimaryBtn full={false} onClick={() => navigate(readingHomeHref())}>Leave exam</PrimaryBtn>
          </div>
        </ModalOverlay>
      )}

      {showSubmitConfirm && (
        <ModalOverlay onClose={() => setShowSubmitConfirm(false)}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6, color: T.ink, fontFamily: T.sans }}>Submit your exam?</div>
          <div style={{ fontSize: 13, color: T.ink2, lineHeight: 1.5, marginBottom: 16, fontFamily: T.sans }}>
            {allQuestionsAnswered
              ? `You answered every question. You still have ${fmtTime(secondsLeft)} on the clock.`
              : `You answered ${answeredCount} of ${totalQuestions} questions. Unanswered questions will be marked wrong.`}
          </div>
          {!allQuestionsAnswered && (
            <div style={{ marginBottom: 16 }}>
              <NudgeBanner>You can keep going — there's still time on the clock.</NudgeBanner>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <GhostBtn onClick={() => setShowSubmitConfirm(false)}>Keep going</GhostBtn>
            <PrimaryBtn full={false} disabled={submitting} onClick={() => { setShowSubmitConfirm(false); handleSubmit(); }}>
              {submitting ? 'Submitting…' : 'Submit ✓'}
            </PrimaryBtn>
          </div>
        </ModalOverlay>
      )}
    </ReadingFrame>
  );
}

function partLabel(n) {
  return ({ 1: 'Signs', 2: 'Gap Fill', 3: 'Text Matching', 4: 'People ↔ Place', 5: 'Long Reading' }[n]) || `Part ${n}`;
}

function hasAnswerForIdx(part, ans, qi) {
  switch (part.part_number) {
    case 1: return !!ans[`sign_${qi}`];
    case 2: return !!ans[`gap_${qi}`];
    case 3: return !!ans[`q_${qi}`];
    case 4: return !!ans[`q_${qi}`];
    case 5: return !!ans[`q_${qi}`];
    default: return false;
  }
}

// Modal overlay used by leave/submit confirmations.
function ModalOverlay({ children, onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(42,38,33,.4)',
        display: 'grid', placeItems: 'center', zIndex: 60,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: T.surface, margin: 16, padding: 20, borderRadius: 20,
          boxShadow: '0 2px 6px rgba(70,55,30,.08), 0 12px 28px rgba(70,55,30,.08)',
          width: 'min(420px, calc(100% - 32px))',
        }}
      >{children}</div>
    </div>
  );
}

// ── Per-part exam-mode question renderer (no feedback in exam mode) ──
function PartQuestion({ part, qIdx, answers, onAnswer }) {
  switch (part.part_number) {
    case 1: return <ExamPart1 part={part} qIdx={qIdx} answers={answers} onAnswer={onAnswer} />;
    case 2: return <ExamPart2 part={part} qIdx={qIdx} answers={answers} onAnswer={onAnswer} />;
    case 3: return <ExamPart3 part={part} qIdx={qIdx} answers={answers} onAnswer={onAnswer} />;
    case 4: return <ExamPart4 part={part} qIdx={qIdx} answers={answers} onAnswer={onAnswer} />;
    case 5: return <ExamPart5 part={part} qIdx={qIdx} answers={answers} onAnswer={onAnswer} />;
    default: return null;
  }
}

function ExamPart1({ part, qIdx, answers, onAnswer }) {
  const sign = (part.content?.signs || [])[qIdx] || {};
  const opts = sign.options || [];
  const picked = answers[`sign_${qIdx}`] || null;
  return (
    <>
      <div style={{ background: T.surface2, border: `2px dashed ${T.hairlineInk}`, borderRadius: 14, padding: 16 }}>
        {(sign.from || sign.to) && (
          <div style={{ fontSize: 11, color: T.ink3, marginBottom: 8, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', fontFamily: T.sans }}>
            Note · {sign.from ? `from ${sign.from}` : ''}{sign.from && sign.to ? ' to ' : ''}{sign.to ? sign.to : ''}
          </div>
        )}
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, fontFamily: T.serif, color: T.ink }}>{sign.body || sign.text || '—'}</p>
        {sign.signoff && <div style={{ marginTop: 14, fontSize: 14, fontFamily: T.serif, color: T.ink }}>— {sign.signoff}</div>}
      </div>
      <div style={{ marginTop: 16, fontSize: 14, fontWeight: 600, color: T.ink, fontFamily: T.sans }}>{sign.question}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
        {opts.map((o) => {
          const letter = String(o.letter || '').toUpperCase();
          const text = typeof o === 'string' ? o : (o.text || '');
          const isPicked = picked === letter;
          return (
            <button
              key={letter}
              type="button"
              onClick={() => onAnswer(`sign_${qIdx}`, letter)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 12, padding: 14,
                background: isPicked ? T.blueBg : T.surface,
                border: `1.5px solid ${isPicked ? T.blue : T.hairlineInk}`,
                borderRadius: 14, color: T.ink,
                fontSize: 15, lineHeight: 1.45, textAlign: 'left',
                cursor: 'pointer', fontFamily: T.sans, width: '100%',
              }}
            >
              <span style={{
                flexShrink: 0, width: 28, height: 28, borderRadius: 14,
                background: isPicked ? T.blue : T.bgSunk,
                color: isPicked ? '#fff' : T.ink2,
                display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 700,
              }}>{letter}</span>
              <span style={{ flex: 1, paddingTop: 4 }}>{text}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}

function ExamPart2({ part, qIdx, answers, onAnswer }) {
  const c = part.content || {};
  const gap = (c.gaps || [])[qIdx] || {};
  const opts = Array.isArray(gap.options) ? gap.options : [];
  const picked = answers[`gap_${qIdx}`] || null;
  return (
    <>
      {c.topic && (
        <div style={{ fontSize: 11, color: T.ink3, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 6, fontFamily: T.sans }}>
          Passage · {c.topic}
        </div>
      )}
      <div style={{ background: T.surface, borderRadius: 14, border: `1px solid ${T.hairline}`, padding: 14 }}>
        <RenderGapPassage passage={c.passage || ''} answers={answers} currentIdx={qIdx} />
      </div>
      <div style={{ marginTop: 14, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.7, color: T.ink3, fontWeight: 700, fontFamily: T.sans }}>
        Gap {gap.n || qIdx + 1}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 8 }}>
        {opts.map((o) => {
          const isPicked = picked === o;
          return (
            <button
              key={o}
              type="button"
              onClick={() => onAnswer(`gap_${qIdx}`, o)}
              style={{
                padding: '16px 10px',
                background: isPicked ? T.blueBg : T.surface,
                border: `1.5px solid ${isPicked ? T.blue : T.hairlineInk}`,
                borderRadius: 14,
                color: T.ink,
                fontSize: 16, fontWeight: 700, fontFamily: T.serif, textAlign: 'center',
                cursor: 'pointer',
              }}
            >{o}</button>
          );
        })}
      </div>
    </>
  );
}

function RenderGapPassage({ passage, answers, currentIdx }) {
  const re = /\[GAP(\d+)\]/g;
  const out = [];
  let last = 0;
  let m;
  let key = 0;
  re.lastIndex = 0;
  while ((m = re.exec(passage)) !== null) {
    if (m.index > last) out.push(<span key={key++}>{passage.slice(last, m.index)}</span>);
    const n = Number(m[1]);
    const idx = n - 1;
    if (idx === currentIdx) {
      out.push(
        <span key={key++} style={{
          display: 'inline-block', margin: '0 2px', padding: '1px 14px',
          background: T.blueBg, border: `2px dashed ${T.blue}`,
          color: T.blueInk, borderRadius: 6, fontWeight: 700, fontFamily: T.sans,
        }}>({n}) ?</span>
      );
    } else if (answers[`gap_${idx}`]) {
      out.push(
        <span key={key++} style={{
          display: 'inline-block', margin: '0 2px', padding: '1px 8px',
          background: T.bgSunk, border: `1px solid ${T.hairlineInk}`,
          color: T.ink2, borderRadius: 6, fontWeight: 600, fontFamily: T.sans,
        }}>{answers[`gap_${idx}`]}</span>
      );
    } else {
      out.push(
        <span key={key++} style={{
          display: 'inline-block', margin: '0 2px', padding: '1px 8px',
          background: T.bgSunk, border: `1px solid ${T.hairlineInk}`,
          color: T.ink3, borderRadius: 6, fontWeight: 600, fontFamily: T.sans,
        }}>({n})</span>
      );
    }
    last = m.index + m[0].length;
  }
  if (last < passage.length) out.push(<span key={key++}>{passage.slice(last)}</span>);
  return <div style={{ fontSize: 14, lineHeight: 1.95, fontFamily: T.serif, color: T.ink }}>{out}</div>;
}

function ExamPart3({ part, qIdx, answers, onAnswer }) {
  const c = part.content || {};
  const people = c.people || [];
  const q = (c.questions || [])[qIdx] || {};
  const picked = answers[`q_${qIdx}`] || null;
  const [activeTab, setActiveTab] = React.useState(people[0]?.id || '');
  const activePerson = people.find((p) => p.id === activeTab) || people[0];
  return (
    <>
      <div style={{ padding: '12px 14px', background: T.surface, borderRadius: 14, border: `1px solid ${T.hairline}`, marginBottom: 12 }}>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.7, color: T.ink3, fontWeight: 700, marginBottom: 4, fontFamily: T.sans }}>Question</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: T.ink, fontFamily: T.sans }}>{q.text}</div>
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {people.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setActiveTab(p.id)}
            style={{
              flex: 1, minWidth: 80, padding: '9px 6px', borderRadius: 14,
              fontSize: 13, fontWeight: 700, textAlign: 'center',
              background: activeTab === p.id ? (p.color || T.p3) : T.surface,
              color: activeTab === p.id ? '#fff' : T.ink2,
              border: `1.5px solid ${activeTab === p.id ? (p.color || T.p3) : T.hairline}`,
              cursor: 'pointer', fontFamily: T.sans,
            }}
          >{p.name}</button>
        ))}
      </div>
      {activePerson && (
        <div style={{
          padding: '14px 16px', background: T.surface, borderRadius: 14,
          border: `1px solid ${T.hairline}`,
          borderLeft: `4px solid ${activePerson.color || T.p3}`,
          fontSize: 14, lineHeight: 1.7, fontFamily: T.serif, color: T.ink,
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: activePerson.color || T.p3, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 6, fontFamily: T.sans }}>
            {activePerson.name}
          </div>
          {activePerson.text}
        </div>
      )}
      <div style={{ marginTop: 18, fontSize: 12, color: T.ink3, fontWeight: 600, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4, fontFamily: T.sans }}>Your answer</div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(people.length, 3)}, 1fr)`, gap: 8 }}>
        {people.map((p) => {
          const isPicked = picked === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onAnswer(`q_${qIdx}`, p.id)}
              style={{
                padding: '14px 8px',
                background: isPicked ? T.blueBg : T.surface,
                border: `1.5px solid ${isPicked ? T.blue : T.hairlineInk}`,
                borderRadius: 14,
                fontSize: 13, fontWeight: 700, textAlign: 'center',
                cursor: 'pointer', fontFamily: T.sans, color: T.ink,
              }}
            >
              <div style={{ width: 8, height: 8, borderRadius: 4, background: p.color || T.ink2, margin: '0 auto 6px' }} />
              {p.name}
            </button>
          );
        })}
      </div>
    </>
  );
}

function ExamPart4({ part, qIdx, answers, onAnswer }) {
  const item = (part.content?.items || [])[qIdx] || {};
  const place = item.place || {};
  const people = Array.isArray(item.people) ? item.people : [];
  const picked = answers[`q_${qIdx}`] || null;
  return (
    <>
      <div style={{ fontSize: 14, color: T.ink2, marginBottom: 10, fontFamily: T.sans }}>Who is this place right for?</div>
      <div style={{
        padding: '14px 16px', background: T.surface, borderRadius: 14,
        border: `1px solid ${T.hairline}`,
        borderLeft: `4px solid ${T.p4}`,
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.p4, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 4, fontFamily: T.sans }}>Place</div>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6, color: T.ink, fontFamily: T.sans }}>{place.name}</div>
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: T.ink2, fontFamily: T.serif }}>{place.body}</p>
      </div>
      <div style={{ marginTop: 16, marginBottom: 8, fontSize: 12, color: T.ink3, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, fontFamily: T.sans }}>Which person fits?</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {people.map((p) => {
          const isPicked = picked === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onAnswer(`q_${qIdx}`, p.id)}
              style={{
                width: '100%', padding: '12px 14px',
                background: isPicked ? T.blueBg : T.surface,
                border: `1.5px solid ${isPicked ? T.blue : T.hairline}`,
                borderRadius: 14,
                cursor: 'pointer', fontFamily: T.sans, textAlign: 'left',
                display: 'flex', flexDirection: 'column', gap: 4,
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>{p.name}</div>
              <div style={{ fontSize: 13, color: T.ink2, lineHeight: 1.5 }}>{p.need}</div>
            </button>
          );
        })}
      </div>
    </>
  );
}

function ExamPart5({ part, qIdx, answers, onAnswer }) {
  const article = part.content?.article || {};
  const paragraphs = Array.isArray(article.paragraphs) ? article.paragraphs : [];
  const q = (part.content?.questions || [])[qIdx] || {};
  const opts = Array.isArray(q.options) ? q.options : [];
  const picked = answers[`q_${qIdx}`] || null;
  return (
    <>
      <div style={{ padding: '12px 14px', background: T.surface, borderRadius: 14, border: `1px solid ${T.hairline}`, marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.ink3, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 2, fontFamily: T.sans }}>{q.n || qIdx + 1}</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: T.ink, fontFamily: T.sans }}>{q.text}</div>
      </div>
      <div style={{ background: T.surface, borderRadius: 14, border: `1px solid ${T.hairline}`, padding: 14 }}>
        {article.title && <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 2, fontFamily: T.serif, color: T.ink }}>{article.title}</div>}
        {article.byline && <div style={{ fontSize: 12, color: T.ink3, fontStyle: 'italic', marginBottom: 12, fontFamily: T.serif }}>{article.byline}</div>}
        {paragraphs.map((p) => (
          <div key={p.id} style={{ fontSize: 14, lineHeight: 1.75, fontFamily: T.serif, color: T.ink, marginBottom: 8 }}>
            {p.text}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
        {opts.map((o) => {
          const letter = String(o.letter || '').toUpperCase();
          const text = typeof o === 'string' ? o : (o.text || '');
          const isPicked = picked === letter;
          return (
            <button
              key={letter}
              type="button"
              onClick={() => onAnswer(`q_${qIdx}`, letter)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 12, padding: 14,
                background: isPicked ? T.blueBg : T.surface,
                border: `1.5px solid ${isPicked ? T.blue : T.hairlineInk}`,
                borderRadius: 14, color: T.ink,
                fontSize: 15, lineHeight: 1.45, textAlign: 'left',
                cursor: 'pointer', fontFamily: T.sans, width: '100%',
              }}
            >
              <span style={{
                flexShrink: 0, width: 28, height: 28, borderRadius: 14,
                background: isPicked ? T.blue : T.bgSunk,
                color: isPicked ? '#fff' : T.ink2,
                display: 'grid', placeItems: 'center', fontSize: 13, fontWeight: 700,
              }}>{letter}</span>
              <span style={{ flex: 1, paddingTop: 4 }}>{text}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}
